import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden/Hidden";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import ADIChangePasswordActions from "./ADIChangePasswordActions";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import getColor from "@insite/mobius/utilities/getColor";

interface OwnProps {
    handleClose: Function;
    handlePopupOpen?: Function;
    isCompact?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    validatePassword,
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    pageTitle: getCurrentPage(state).fields.title,
});

export interface ChangePasswordStyles {
    actions: HiddenProps;
    passwordRequirementsGridItem: GridItemProps;
    passwordRequirementsGridContainer: GridContainerProps;
    mainRequired: InjectableCss;
    mainRequiredText: TypographyProps;
    passwordGridContainer: GridContainerProps;
    buttonGridContainer: GridContainerProps;
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    userInformationButtonGridItem?: GridItemProps;
    label?: InjectableCss;
    textFieldIcon?: InjectableCss;
    showPasswordsGridItem?: GridItemProps;
    changePasswordLink?: LinkPresentationProps;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
}

export const changePasswordStyles: ChangePasswordStyles = {
    actions: {
        css: css`
            width: 100%;
        `,
    },
    passwordGridContainer: {
        gap: 10,
        css: css`
            > ${GridOffset} {
                display: flex;
                flex-direction: column;
            }
        `,
    },
    buttonGridContainer: {
        gap: 10,
        css: css`
            margin-top: 10px;
        `,
    },
    passwordRequirementsGridContainer: {
        css: css`
            margin-bottom: 10px;
            ul li {
                list-style: disc;
                margin-left: 16px;
                margin-top: 0px;
                font-size: 14px;
            }
        `,
    },
    mainRequired: {
        css: css`
            width: 100%;
        `,
    },
    mainRequiredText: {
        css: css`
            font-size: 14px;
            margin-bottom: 0px;
        `,
    },
    passwordRequirementsGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    showPasswordsGridItem: {
        width: 12,
    },
    label: {
        css: css`
            width: 170px;
        `,
    },
    textFieldIcon: {
        css: css`
            color: ${getColor("primary")};
            font-size: 18px;
        `,
    },
    userInformationGridItem: {
        width: 12,
    },
    userInformationButtonGridItem: {
        width: 12,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    userInformationTitle: {
        variant: "h4",
        as: "h2",
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 150px;
            display: flex;
            align-items: center;
        `,
    },
};

const styles = changePasswordStyles;

const ADIChangePasswordView = ({ validatePassword, handleClose, handlePopupOpen, isCompact }: Props) => {
    const [password, setPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordErrorMessage, setNewPasswordErrorMessage] = useState<ReactNode>("");
    const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
    const [passwordConfirmErrorMessage, setPasswordConfirmErrorMessage] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [showValidation, setShowValidation] = useState(false);
    const [error, setError] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showReEnteredPassword, setShowReEnteredPassword] = useState(false);

    useEffect(() => {
        setError(
            !!newPasswordErrorMessage || !newPassword || !confirmNewPassword || confirmNewPassword !== newPassword,
        );
    }, [newPassword, newPasswordErrorMessage, confirmNewPassword]);

    const currentPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        setPassword(event.currentTarget.value);
    };

    const newPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const newPassword = event.currentTarget.value;

        // validatePassword({
        //     password: newPassword,
        //     onComplete: errorMessage => {
        //         setNewPasswordErrorMessage(errorMessage);
        //     },
        // });
        setNewPasswordErrorMessage("");
        setNewPassword(newPassword);
    };
    

    const confirmNewPasswordChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        setConfirmNewPassword(event.currentTarget.value);
    };

    const confirmNewPasswordError =
        showValidation && newPassword && confirmNewPassword && newPassword !== confirmNewPassword
            ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
            : "";

    const handleShowCurrentPassword = () => {
        setShowCurrentPassword(prev => !prev);
    };

    const handleShowNewPassword = () => {
        setShowNewPassword(prev => !prev);
    };

    const handleShowReEnteredPassword = () => {
        setShowReEnteredPassword(prev => !prev);
    };

    const handleSetPasswordError = (message: string) => {
        setPasswordErrorMessage(message);
    };

    const handleNewPasswordErrorMessage = (message: string) => {
        setNewPasswordErrorMessage(message);
    };
    const handleConfrimPasswordErrorMessage = (message: string) => {
        setPasswordConfirmErrorMessage(message);
    };

    return (
        <StyledWrapper as="form">
            <GridContainer {...styles.passwordRequirementsGridContainer}>
                <GridItem width={12} {...styles.passwordRequirementsGridItem}>
                    <StyledWrapper {...styles.mainRequired}>
                        <Typography variant="p" {...styles.mainRequiredText}>
                            {translate("Your new password must be 8-15 characters and include:")}
                        </Typography>
                        <Typography>{passwordErrorMessage}</Typography>
                    </StyledWrapper>
                    <ul>
                        <li>{translate("One upper case letter")}</li>
                        <li>{translate("One lower case letter")}</li>
                        <li>{translate("One number")}</li>
                        <li>{translate("No special characters such as @, # or %")}</li>
                    </ul>
                </GridItem>
            </GridContainer>
            <Hidden below="md">
                <GridContainer {...styles.passwordGridContainer}>
                    <GridItem width={isCompact ? [12, 12, 6, 6, 6] : 12}>
                        <TextField
                            type={showCurrentPassword ? "text" : "password"}
                            label={translate("Current Password *")}
                            labelProps={{ ...styles.label }}
                            onInput={currentPasswordChangeHandler}
                            autoComplete="current-password"
                            error={(showValidation && !password) || passwordErrorMessage}
                            iconProps={{ src: showCurrentPassword ? Eye : EyeOff, ...styles.textFieldIcon }}
                            iconClickableProps={{ type: "button", onClick: () => handleShowCurrentPassword() }}
                            labelPosition="left"
                        />
                    </GridItem>
                    <GridItem width={isCompact ? [12, 12, 6, 6, 6] : 12}>
                        <TextField
                            type={showNewPassword ? "text" : "password"}
                            label={translate("New Password *")}
                            labelProps={{ ...styles.label }}
                            onInput={newPasswordChangeHandler}
                            error={(showValidation && !newPassword) || (showValidation && newPasswordErrorMessage)}
                            iconProps={{ src: showNewPassword ? Eye : EyeOff, ...styles.textFieldIcon }}
                            iconClickableProps={{ type: "button", onClick: () => handleShowNewPassword() }}
                            autoComplete="new-password"
                            labelPosition="left"
                        />
                    </GridItem>
                    <GridItem width={isCompact ? [12, 12, 6, 6, 6] : 12}>
                        <TextField
                            type={showReEnteredPassword ? "text" : "password"}
                            label={translate("Re-enter Password *")}
                            labelProps={{ ...styles.label }}
                            onInput={confirmNewPasswordChangeHandler}
                            error={(showValidation && !confirmNewPassword) || confirmNewPasswordError}
                            iconProps={{ src: showReEnteredPassword ? Eye : EyeOff, ...styles.textFieldIcon }}
                            iconClickableProps={{
                                type: "button",
                                onClick: () => handleShowReEnteredPassword(),
                            }}
                            autoComplete="new-password"
                            labelPosition="left"
                        />
                    </GridItem>
                </GridContainer>
                <GridContainer {...styles.buttonGridContainer}>
                    <GridItem {...styles.userInformationButtonGridItem}>
                        <Hidden {...styles.actions}>
                            <ADIChangePasswordActions
                                password={password}
                                newPassword={newPassword}
                                newPasswordError={handleSetPasswordError}
                                handleNewPasswordErrorMessage={handleNewPasswordErrorMessage}
                                handleConfrimPasswordErrorMessage={handleConfrimPasswordErrorMessage}
                                confirmNewPassword={confirmNewPassword}
                                error={error}
                                showValidation={showValidation}
                                setShowValidation={setShowValidation}
                                handleClose={handleClose}
                                handlePopupOpenAction={handlePopupOpen}
                            ></ADIChangePasswordActions>
                        </Hidden>
                    </GridItem>
                </GridContainer>
            </Hidden>

            <Hidden above="sm">
                <GridContainer {...styles.passwordGridContainer}>
                    <GridItem width={isCompact ? [12, 12, 6, 6, 6] : 12}>
                        <TextField
                            type={showCurrentPassword ? "text" : "password"}
                            label={translate("Current Password *")}
                            labelProps={{ ...styles.label }}
                            error={passwordErrorMessage}
                            onInput={currentPasswordChangeHandler}
                            autoComplete="current-password"
                            iconProps={{ src: showCurrentPassword ? Eye : EyeOff, ...styles.textFieldIcon }}
                            iconClickableProps={{ type: "button", onClick: () => handleShowCurrentPassword() }}
                        />
                    </GridItem>
                    <GridItem width={isCompact ? [12, 12, 6, 6, 6] : 12}>
                        <TextField
                            type={showNewPassword ? "text" : "password"}
                            label={translate("New Password *")}
                            labelProps={{ ...styles.label }}
                            onInput={newPasswordChangeHandler}
                            error={showValidation && newPasswordErrorMessage}
                            iconProps={{ src: showNewPassword ? Eye : EyeOff, ...styles.textFieldIcon }}
                            iconClickableProps={{ type: "button", onClick: () => handleShowNewPassword() }}
                            autoComplete="new-password"
                        />
                    </GridItem>
                    <GridItem width={isCompact ? [12, 12, 6, 6, 6] : 12}>
                        <TextField
                            type={showReEnteredPassword ? "text" : "password"}
                            label={translate("Re-enter Password *")}
                            labelProps={{ ...styles.label }}
                            onInput={confirmNewPasswordChangeHandler}
                            error={passwordConfirmErrorMessage}
                            iconProps={{ src: showReEnteredPassword ? Eye : EyeOff, ...styles.textFieldIcon }}
                            iconClickableProps={{
                                type: "button",
                                onClick: () => handleShowReEnteredPassword(),
                            }}
                            autoComplete="new-password"
                        />
                    </GridItem>
                </GridContainer>
                <GridContainer {...styles.buttonGridContainer}>
                    <GridItem {...styles.userInformationButtonGridItem}>
                        <Hidden {...styles.actions}>
                            <ADIChangePasswordActions
                                password={password}
                                newPassword={newPassword}
                                newPasswordError={handleSetPasswordError}
                                handleNewPasswordErrorMessage={handleNewPasswordErrorMessage}
                                handleConfrimPasswordErrorMessage={handleConfrimPasswordErrorMessage}
                                confirmNewPassword={confirmNewPassword}
                                error={error}
                                showValidation={showValidation}
                                setShowValidation={setShowValidation}
                                handleClose={handleClose}
                                handlePopupOpenAction ={handlePopupOpen}
                            ></ADIChangePasswordActions>
                        </Hidden>
                    </GridItem>
                </GridContainer>
            </Hidden>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ADIChangePasswordView);
